import { ReactElement, ReactNode, useMemo } from "react";
import { useMatch, useParams } from "react-router-dom";
import useExternalBuckets from "files/hooks/useExternalBuckets";
import { getBaseDirectorySubtitle, resolveRouterPath, useCurrentPath } from "files/hooks/useFiles";
import FileInfo from "files/types/interfaces/fileInfo";
import { AwsIcon } from "~/assets/icons";
import useInfiniteScrollSentinel from "~/hooks/useInfiniteScrollSentinel";
import { cn } from "~/lib/utils";
import useIsWorkbenchFileContext from "~/pages/files/hooks/useIsWorkbenchFileContext";
import { FileRow, OnRowClickParams } from "./file-row";
import { Label } from "./label";
import { ListCard } from "./list-card";

const useUrlNavigation = ({ loading }: { loading: boolean }) => {
  const directoryPath = useCurrentPath();
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const isRoot = !directoryPath;
  const { data, isLoading } = useExternalBuckets();
  const showOtherLocations = !loading && isRoot && !!data?.length;

  return {
    isGlobalFileContext,
    directoryPath,
    showOtherLocations,
    externalBuckets: data,
    isExternalBucketsLoading: isLoading,
  };
};

const DirectoryHeader = ({ hideAllActions }: { hideAllActions?: boolean }) => {
  return (
    <Label className="flex rounded-t-lg bg-background gap-8 px-5 py-3 font-medium border-b border-gray-300 text-[13px] text-gray-900/50">
      <span className="grow shrink basis-0 text-nowrap">Name</span>
      <span className="w-[100px] text-end">Size</span>
      <span className={cn("w-[236px]", hideAllActions && "w-fit")}>Updated</span>
    </Label>
  );
};

interface OtherLocation {
  fileKey: string;
  description: string;
}

type DirectoryProps = {
  files: FileInfo[];
  loading: boolean;
  hideAllActions?: boolean;
  className?: string;
  listCardClassName?: string;
  showExternalBuckets?: boolean;
  error?: ReactNode;
  hideMainLocationsHeader?: boolean;
  /** When true, external buckets are rendered inline in the primary list instead of a separate "Other Locations" section. */
  compact?: boolean;
} & (
  | {
      navigation: "url-based";
      currentPath?: undefined;
      otherLocations?: undefined;
      onRowClick?: undefined;
      selectedFileKey?: undefined;
      /**
       * Relative path the parent-navigation ".." row points at, for listings whose root is
       * not the directory's own parent. An embedded browser scoped to a prefix uses this to
       * send the user back to its own root instead of climbing outside that scope.
       */
      backTo?: string;
      /** Whether more items can be loaded from the server. Enables infinite scroll when true. */
      hasMore?: boolean;
      /** Whether a subsequent page is currently being fetched. */
      isLoadingMore?: boolean;
      /** Callback fired when the user scrolls near the bottom of the list. */
      onLoadMore?: () => void;
    }
  | {
      navigation: "in-memory";
      currentPath: string;
      onRowClick?: (params: OnRowClickParams) => void;
      selectedFileKey?: string;
      /** These are only shown if `loading === false` */
      otherLocations?: OtherLocation[];
      /**
       * When set, the parent-navigation ".." row is hidden when currentPath equals this value.
       * Use this to prevent navigating above a constrained root directory.
       */
      rootPath?: string;
    }
);

const Directory = ({
  currentPath,
  files,
  otherLocations: externalLocations,
  loading,
  navigation,
  hideAllActions,
  selectedFileKey,
  onRowClick,
  className,
  listCardClassName,
  showExternalBuckets,
  error,
  hideMainLocationsHeader,
  compact,
  ...rest
}: DirectoryProps) => {
  const rootPath =
    navigation === "in-memory" ? (rest as { rootPath?: string }).rootPath : undefined;
  const backTo = navigation === "url-based" ? (rest as { backTo?: string }).backTo : undefined;
  const hasMore = navigation === "url-based" ? (rest as { hasMore?: boolean }).hasMore : undefined;
  const isLoadingMore =
    navigation === "url-based" ? (rest as { isLoadingMore?: boolean }).isLoadingMore : undefined;
  const onLoadMore =
    navigation === "url-based" ? (rest as { onLoadMore?: () => void }).onLoadMore : undefined;

  const sentinelRef = useInfiniteScrollSentinel<HTMLDivElement>({ hasMore, onLoadMore });

  const { directoryPath, isGlobalFileContext, externalBuckets, isExternalBucketsLoading } =
    useUrlNavigation({
      loading,
    });
  const { datasetId } = useParams();
  const isRoot =
    navigation === "url-based"
      ? !directoryPath
      : rootPath
        ? currentPath === rootPath
        : currentPath === "" || currentPath === "/";

  const shouldShowOtherLocations =
    showExternalBuckets &&
    !loading &&
    isRoot &&
    (isExternalBucketsLoading || !!externalBuckets?.length);

  const rows = useMemo(() => {
    const backNav: ReactElement[] = [];

    if (!isRoot) {
      backNav.push(
        <FileRow
          key="back"
          fileKey={backTo ?? ".."}
          currentPath={currentPath}
          hideActions
          hideAllActions={hideAllActions}
          navigation={navigation}
          onRowClick={onRowClick}
          selectedFileKey={selectedFileKey}
        />
      );
    }

    return [
      ...backNav,
      ...files.map((node) => {
        const resolvedPath = resolveRouterPath(node.key, isGlobalFileContext, datasetId);
        const subtitle = getBaseDirectorySubtitle(resolvedPath, datasetId);

        return (
          <FileRow
            key={node.key}
            fileKey={node.key}
            currentPath={currentPath}
            navigation={navigation}
            hideAllActions={hideAllActions}
            fileMetadata={{
              subtitle,
              type: node.type,
              size: node.size,
              pendingAction: node.pending_action,
              updatedAt: node.updated_at,
              s3Uri: node.s3_uri,
              badge: node.badge,
            }}
            onRowClick={onRowClick}
            selectedFileKey={selectedFileKey}
          />
        );
      }),
    ];
  }, [
    isRoot,
    files,
    currentPath,
    hideAllActions,
    navigation,
    onRowClick,
    selectedFileKey,
    isGlobalFileContext,
    datasetId,
    backTo,
  ]);

  const otherLocations: OtherLocation[] | undefined =
    showExternalBuckets && isRoot
      ? externalBuckets?.map(({ bucket_name, description }) => ({
          fileKey: bucket_name,
          description,
        }))
      : externalLocations;

  if (error) {
    return (
      <p className="text-destructive mb-2 text-sm">
        There was an error loading the S3 files. Please try again later.
      </p>
    );
  }

  const externalBucketRows = shouldShowOtherLocations
    ? otherLocations?.map(({ fileKey, description }) => (
        <FileRow
          key={fileKey}
          fileKey={fileKey}
          currentPath={currentPath}
          navigation={navigation}
          hideAllActions={hideAllActions}
          hideActions
          fileMetadata={{
            subtitle: description,
            badge: <AwsIcon width={24} />,
          }}
          onRowClick={onRowClick}
          selectedFileKey={selectedFileKey}
        />
      ))
    : null;

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <ListCard
        className={cn("gap-1 flex flex-col", listCardClassName)}
        header={
          hideMainLocationsHeader ? null : <DirectoryHeader hideAllActions={hideAllActions} />
        }
        loading={loading}
        placeholder={{ className: "h-[34px]", count: 12 }}
      >
        {rows}
        {compact && externalBucketRows}
        {/* Sentinel observed by IntersectionObserver to trigger loading the next page */}
        {hasMore && <div ref={sentinelRef} className="h-px" aria-hidden />}
        {isLoadingMore && (
          <div className="flex justify-center py-3">
            <span className="text-sm text-gray-500">Loading more...</span>
          </div>
        )}
      </ListCard>
      {!compact && shouldShowOtherLocations && (
        <ListCard
          className={cn("gap-1", listCardClassName)}
          header={
            <Label className="px-5 py-3 text-[13px] text-gray-900/50 font-medium border-b border-gray-300">
              Other Locations
            </Label>
          }
          loading={isExternalBucketsLoading}
          placeholder={{ className: "h-[34px]" }}
        >
          {externalBucketRows}
        </ListCard>
      )}
    </div>
  );
};

export { Directory, type OtherLocation, type DirectoryProps };
